import { omit } from 'lodash';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';

import ComposeModalHeader from '../../../components/Compose/ComposeModal/ComposeModalHeader';

import useParamTopicId from '../../../hooks/router/params/useParamTopicId';

import useComposeType from '../../../hooks/router/search/useComposeType';

import { DraftDataFormatted } from '../../../models/draft';
import { ComposeTypeParam } from '../../../models/pageParams';

import { mapStateToProps } from './redux';
import { Props } from './types';

/**
 * Container for compose modal header
 *
 * @param props                Props passed to the container
 * @param props.draft          Current state of the draft
 * @param props.fallbackTitle  The fallback title in case there is no typed title
 * @param props.isDraftChanged Whether the draft has been changed
 * @param props.nodeType       The type of the compose form
 * @param props.onClose        Request to close the modal
 * @param props.status         The status of the compose form
 * @param props.title          The typed value
 * @param props.titleId        The ID of the title element
 * @param props.topicId        The ID of the topic
 * @returns                    The container itself
 */
const ComposeModalHeaderContainer: FC<Props> = ({
  draft,
  fallbackTitle,
  isDraftChanged,
  nodeType,
  onClose,
  status,
  title,
  titleId,
  topicId,
}) => {
  const initialDraftRef = useRef<DraftDataFormatted | null>(null);

  const composeType = useComposeType();
  const topicIdParam = useParamTopicId();

  useEffect(() => {
    if (initialDraftRef.current !== null || draft?.nodeId === null) {
      return;
    }

    initialDraftRef.current = draft;
  }, [draft]);

  const isDefaultTopicChanged = topicId !== topicIdParam;
  const isStandalone = titleId === undefined;
  const headerTitle = isStandalone ? fallbackTitle : title || fallbackTitle;

  const isComposeDraftChanged = useMemo(() => {
    if (nodeType === 'MESSAGE') {
      return isDraftChanged || isDefaultTopicChanged;
    }

    // Due date is not needed for templates
    return (
      JSON.stringify(omit(draft, ['dateDueCurrent', 'dateDueSaved'])) !==
      JSON.stringify(
        omit(initialDraftRef.current, ['dateDueCurrent', 'dateDueSaved']),
      )
    );
  }, [draft, isDefaultTopicChanged, isDraftChanged, nodeType]);

  const defaultComposeType: ComposeTypeParam = useMemo(() => {
    if (composeType !== null) {
      return composeType;
    }
    return status === null ? 'post' : 'task';
  }, [composeType, status]);

  return (
    <ComposeModalHeader
      composeType={defaultComposeType}
      isDraftChanged={isComposeDraftChanged}
      nodeType={nodeType}
      onClose={onClose}
      title={headerTitle}
    />
  );
};

export default connect(mapStateToProps)(ComposeModalHeaderContainer);
